import { z } from 'zod';
import {
    CardPointeCnpTokenSaleOutcome,
    type CardPointeCnpTokenSaleApprovedBody,
    type CardPointeCnpTokenSaleApprovedResponseDetails,
    type CardPointeCnpTokenSaleAuthorizationBody,
    type CardPointeCnpTokenSaleDeclinedBody,
    type CardPointeCnpTokenSaleDeclinedResponseDetails,
    type CardPointeCnpTokenSaleMalformedResponseDetails,
    type CardPointeCnpTokenSaleRequestBody,
    type CardPointeCnpTokenSaleRequestDetails,
    type CardPointeCnpTokenSaleResponseDetails,
} from '../../../types/ipg/cardpointe/cardpointe-cnp-token-sale.types';

export const CardPointeCnpTokenSaleAmountSchema: z.ZodString = z
    .string()
    .regex(/^\d+(?:\.\d{2})?$/);

const CardPointeCnpTokenSaleRequestBodyBaseSchema: z.ZodObject<{
    merchid: z.ZodString;
    account: z.ZodString;
    amount: z.ZodString;
    currency: z.ZodString;
    capture: z.ZodUnion<readonly [z.ZodLiteral<'y'>, z.ZodLiteral<'n'>]>;
    ecomind: z.ZodUnion<readonly [z.ZodLiteral<'T'>, z.ZodLiteral<'E'>]>;
    orderid: z.ZodOptional<z.ZodString>;
    name: z.ZodOptional<z.ZodString>;
    expiry: z.ZodOptional<z.ZodString>;
    address: z.ZodOptional<z.ZodString>;
    city: z.ZodOptional<z.ZodString>;
    region: z.ZodOptional<z.ZodString>;
    country: z.ZodOptional<z.ZodString>;
    postal: z.ZodOptional<z.ZodString>;
}> = z.object({
    merchid: z.string().min(1),
    account: z.string().min(1),
    amount: CardPointeCnpTokenSaleAmountSchema,
    currency: z.string().length(3),
    capture: z.union([z.literal('y'), z.literal('n')]),
    ecomind: z.union([z.literal('T'), z.literal('E')]),
    orderid: z.string().min(1).optional(),
    name: z.string().min(1).optional(),
    expiry: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
    address: z.string().min(1).optional(),
    city: z.string().min(1).optional(),
    region: z.string().min(1).optional(),
    country: z.string().min(1).optional(),
    postal: z.string().min(1).optional(),
});

export const CardPointeCnpTokenSaleRequestBodySchema: z.ZodType<CardPointeCnpTokenSaleRequestBody> =
    z.union([
        CardPointeCnpTokenSaleRequestBodyBaseSchema.strict(),
        CardPointeCnpTokenSaleRequestBodyBaseSchema.extend({
            cof: z.literal('C'),
            cofscheduled: z.literal('N'),
        }).strict(),
        CardPointeCnpTokenSaleRequestBodyBaseSchema.extend({
            cof: z.literal('M'),
            cofscheduled: z.literal('N'),
        }).strict(),
        CardPointeCnpTokenSaleRequestBodyBaseSchema.extend({
            cof: z.literal('M'),
            cofscheduled: z.literal('Y'),
        }).strict(),
    ]);

export const CardPointeCnpTokenSaleRequestDetailsSchema: z.ZodObject<{
    method: z.ZodLiteral<'PUT'>;
    path: z.ZodLiteral<'/cardconnect/rest/auth'>;
    body: typeof CardPointeCnpTokenSaleRequestBodySchema;
}> = z.object({
    method: z.literal('PUT'),
    path: z.literal('/cardconnect/rest/auth'),
    body: CardPointeCnpTokenSaleRequestBodySchema,
}) satisfies z.ZodType<CardPointeCnpTokenSaleRequestDetails>;

export const CardPointeCnpTokenSaleAuthorizationBodySchema: z.ZodObject<{
    respstat: z.ZodUnion<readonly [z.ZodLiteral<'A'>, z.ZodLiteral<'B'>, z.ZodLiteral<'C'>]>;
    amount: z.ZodOptional<z.ZodString>;
    resptext: z.ZodOptional<z.ZodString>;
    authcode: z.ZodOptional<z.ZodString>;
    respcode: z.ZodOptional<z.ZodString>;
    merchid: z.ZodOptional<z.ZodString>;
    cvvresp: z.ZodOptional<z.ZodString>;
    retref: z.ZodOptional<z.ZodString>;
    avsresp: z.ZodOptional<z.ZodString>;
    respproc: z.ZodOptional<z.ZodString>;
    batchid: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    account: z.ZodOptional<z.ZodString>;
    expiry: z.ZodOptional<z.ZodString>;
    name: z.ZodOptional<z.ZodString>;
    entrymode: z.ZodOptional<z.ZodString>;
    bintype: z.ZodOptional<z.ZodString>;
    commcard: z.ZodOptional<z.ZodString>;
    orderid: z.ZodOptional<z.ZodString>;
    fee_amount: z.ZodOptional<z.ZodString>;
    fee_type: z.ZodOptional<
        z.ZodUnion<readonly [z.ZodLiteral<'SURCHRG'>, z.ZodLiteral<'SURCHRG_WAIVED'>]>
    >;
    fee_value: z.ZodOptional<z.ZodString>;
    fee_format: z.ZodOptional<z.ZodUnion<readonly [z.ZodLiteral<'percent'>, z.ZodLiteral<''>]>>;
    fee_authcode: z.ZodOptional<z.ZodString>;
    fee_merchid: z.ZodOptional<z.ZodString>;
    fee_retref: z.ZodOptional<z.ZodString>;
}> = z.object({
    respstat: z.union([z.literal('A'), z.literal('B'), z.literal('C')]),
    amount: z.string().optional(),
    resptext: z.string().optional(),
    authcode: z.string().optional(),
    respcode: z.string().optional(),
    merchid: z.string().optional(),
    cvvresp: z.string().optional(),
    retref: z.string().optional(),
    avsresp: z.string().optional(),
    respproc: z.string().optional(),
    batchid: z.string().optional(),
    token: z.string().optional(),
    account: z.string().optional(),
    expiry: z.string().optional(),
    name: z.string().optional(),
    entrymode: z.string().optional(),
    bintype: z.string().optional(),
    commcard: z.string().optional(),
    orderid: z.string().optional(),
    cof: z.union([z.literal('C'), z.literal('M')]).optional(),
    fee_amount: CardPointeCnpTokenSaleAmountSchema.optional(),
    fee_type: z.union([z.literal('SURCHRG'), z.literal('SURCHRG_WAIVED')]).optional(),
    fee_value: z.string().regex(/^\d+$/).optional(),
    fee_format: z.union([z.literal('percent'), z.literal('')]).optional(),
    fee_authcode: z.string().optional(),
    fee_merchid: z.string().optional(),
    fee_retref: z.string().optional(),
}) satisfies z.ZodType<CardPointeCnpTokenSaleAuthorizationBody>;

export const CardPointeCnpTokenSaleApprovedBodySchema =
    CardPointeCnpTokenSaleAuthorizationBodySchema.extend({
        respstat: z.literal('A'),
        retref: z.string().min(1),
        amount: z.string().min(1),
        merchid: z.string().min(1),
    }) satisfies z.ZodType<CardPointeCnpTokenSaleApprovedBody>;

export const CardPointeCnpTokenSaleDeclinedBodySchema =
    CardPointeCnpTokenSaleAuthorizationBodySchema.extend({
        respstat: z.union([z.literal('B'), z.literal('C')]),
        resptext: z.string().min(1),
    }) satisfies z.ZodType<CardPointeCnpTokenSaleDeclinedBody>;

export const CardPointeCnpTokenSaleApprovedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeCnpTokenSaleOutcome.APPROVED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CardPointeCnpTokenSaleApprovedBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeCnpTokenSaleOutcome.APPROVED),
    httpStatus: z.literal(200),
    body: CardPointeCnpTokenSaleApprovedBodySchema,
}) satisfies z.ZodType<CardPointeCnpTokenSaleApprovedResponseDetails>;

export const CardPointeCnpTokenSaleDeclinedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeCnpTokenSaleOutcome.DECLINED>;
    httpStatus: z.ZodLiteral<200>;
    body: typeof CardPointeCnpTokenSaleDeclinedBodySchema;
}> = z.object({
    outcome: z.literal(CardPointeCnpTokenSaleOutcome.DECLINED),
    httpStatus: z.literal(200),
    body: CardPointeCnpTokenSaleDeclinedBodySchema,
}) satisfies z.ZodType<CardPointeCnpTokenSaleDeclinedResponseDetails>;

export const CardPointeCnpTokenSaleMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeCnpTokenSaleOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CardPointeCnpTokenSaleOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
}) satisfies z.ZodType<CardPointeCnpTokenSaleMalformedResponseDetails>;

export const CardPointeCnpTokenSaleResponseDetailsSchema: z.ZodType<CardPointeCnpTokenSaleResponseDetails> =
    z.discriminatedUnion('outcome', [
        CardPointeCnpTokenSaleApprovedResponseDetailsSchema,
        CardPointeCnpTokenSaleDeclinedResponseDetailsSchema,
        CardPointeCnpTokenSaleMalformedResponseDetailsSchema,
    ]);
